import { Frequency, RRule, RRuleSet, rrulestr, Weekday } from 'rrule';
import {
    tzidOfRuleString,
    instantToFloating,
    floatingIsoToTrueIso,
    toFloatingRuleString,
    addTzidToRuleString,
    untilFloatingToTrue,
} from './tzid-rule-frame';

/**
 * BE#648 — set while rewriting a MIGRATED (TZID) series. In this mode the public
 * methods have already normalised the rule + input instants into the FLOATING frame
 * (wall-clock in UTC fields), so `toRuleTimeBase`/`fromRuleTimeBase` — which exist to
 * reconcile the DST-adjusted display frame with the rule frame for LEGACY series —
 * must be no-ops: displayed == rule == floating. Synchronous + never reentrant (the
 * handler methods do no async), set/reset in a try/finally in each wrapper.
 */
let FLOATING_TIME_BASE = false;

export interface RecurrenceHandlerResult {
  originalAction: 'patch' | 'delete';
  originalNewRRule?: string;
  originalDueDate: string;

  newAction: 'create' | 'none';
  newRRule?: string;
  newDueDate?: string;
}

type Scope = 'this' | 'thisAndFollowing' | 'all';

/**
 * Translate an occurrence AS THE CALENDAR DREW IT into the instant the rule
 * itself emits.
 *
 * The calendar (and the backend's `calculateNextDuedate`) hold the local
 * wall-clock steady across a DST change, so a 10:00 series stays 10:00 and its
 * UTC instant moves by an hour; the rrule engine keeps emitting the original
 * instant. Everything below writes back INTO the rule — an EXDATE for one
 * occurrence, an UNTIL that splits a series, a first-occurrence comparison — so
 * it must speak the rule's time base. Writing the on-screen instant instead
 * produced an EXDATE that matched no occurrence: the occurrence stayed on the
 * calendar and a copy appeared at the new date.
 *
 * This is the inverse of bt-common's `adjustForDSTChange`, which is what puts
 * the occurrence into wall-clock terms in the first place. Kept here rather
 * than imported because bt-common is a submodule shared with the backend, and
 * this direction is only ever needed on the calendar's write path.
 */
const toRuleTimeBase = (occurrence: Date, ruleAnchor: Date | undefined): Date => {
    if (FLOATING_TIME_BASE) return occurrence; // tz path: displayed == rule == floating
    const ruleOffset = ruleAnchor?.getTimezoneOffset();
    if (ruleOffset === undefined) return occurrence;
    const occurrenceOffset = occurrence.getTimezoneOffset();
    if (occurrenceOffset === ruleOffset) return occurrence;
    return new Date(occurrence.getTime() + (ruleOffset - occurrenceOffset) * 60_000);
};

/**
 * The other direction: an instant the RULE emits, expressed as the instant the
 * message actually goes out.
 *
 * This is the same arithmetic bt-common's `adjustForDSTChange` does, and it is
 * the base a stored `dueDate` is in. Anything read out of the rule -- the next
 * unexcluded occurrence, a re-seated DTSTART -- is a rule instant, so it has to
 * come back through here before it is handed back as a dueDate, or the document
 * ends up due an hour off the wall clock the customer chose.
 *
 * Symmetric with `toRuleTimeBase` by construction: each side derives the
 * runtime offset from the instant it is holding, so `from(to(x)) === x`.
 */
const fromRuleTimeBase = (ruleInstant: Date, ruleAnchor: Date | undefined): Date => {
    if (FLOATING_TIME_BASE) return ruleInstant; // tz path: rule == stored == floating
    const ruleOffset = ruleAnchor?.getTimezoneOffset();
    if (ruleOffset === undefined) return ruleInstant;
    const wallOffset = ruleInstant.getTimezoneOffset();
    if (wallOffset === ruleOffset) return ruleInstant;
    return new Date(ruleInstant.getTime() + (wallOffset - ruleOffset) * 60_000);
};

/**
 * Move a rule's DTSTART forward without changing how many sends are left.
 *
 * Both write paths re-seat DTSTART onto the document's current `dueDate` so the
 * rule speaks the right time-of-day. For a COUNT-bounded series that has already
 * fired, that silently re-arms the whole budget: COUNT=5 starting from the third
 * occurrence emits five MORE, so a customer with three sends left ends up with
 * five, and deleting one occurrence could add two. COUNT is a promise about how
 * many messages get sent, so moving the start has to spend the occurrences the
 * series has already used.
 *
 * Mutates in place, because the callers go on to read `mainRule.options` (the
 * first-occurrence checks compare against `dtstart`).
 *
 * No-ops for an unbounded or UNTIL-bounded rule (nothing to conserve) and when
 * the start is not actually moving forward.
 */
const rebaseDtstartConservingCount = (rule: RRule, newDtstart: Date): void => {
  const previousDtstart = rule.options.dtstart;
  const count = rule.options.count;

  if (count && previousDtstart && newDtstart.getTime() > previousDtstart.getTime()) {
    // Bounded by COUNT, so `all()` terminates.
    const alreadySpent = rule.all().filter(occurrence => occurrence.getTime() < newDtstart.getTime()).length;
    // Never below 1: a rule with COUNT=0 is not "no occurrences", it is UNBOUNDED,
    // which would turn a finite series into one that sends forever.
    rule.options.count = Math.max(1, count - alreadySpent);
  }

  // `newDtstart` is a RULE instant, in the base the OLD DTSTART defines. But a
  // rule's time base IS its DTSTART's UTC offset -- the planner
  // (`adjustOccurrenceForDST`) and the sender (`calculateNextDuedate`'s
  // `offsetRrule`) both read it from there -- so re-seating DTSTART on the far
  // side of a clock change changes the base. Writing the old-base instant made
  // every remaining send an hour off (FAIL B: a 09:00 series drawn at 10:00, and
  // the backend sending the due occurrence twice). So the anchor is written as
  // the WALL-CLOCK instant, and everything else the rule holds in UTC terms
  // (BYHOUR/BYMINUTE, UNTIL) moves by the same amount; callers re-express their
  // EXDATEs and working dates against the new anchor. With no clock change
  // between the two anchors the shift is zero and nothing but DTSTART moves.
  const wallStart = previousDtstart ? fromRuleTimeBase(newDtstart, previousDtstart) : newDtstart;
  const shiftMinutes = Math.round((wallStart.getTime() - newDtstart.getTime()) / 60_000);
  rule.options.dtstart = new Date(wallStart.getTime());
  if (shiftMinutes !== 0) shiftRuleTimeOfDay(rule, shiftMinutes);
};

/**
 * Move a rule's UTC time-of-day constraints by `minutes`, for a change of time
 * base. If that carries the time across UTC midnight, the day constraints move
 * a day with it (plain and nth weekdays keep their ordinal). A BYMONTHDAY that
 * would leave 1..28 is left alone -- a month-edge case this cannot express
 * without knowing each month's length.
 */
const shiftRuleTimeOfDay = (rule: RRule, minutes: number): void => {
  const o: any = rule.options;
  if (o.until) o.until = new Date(o.until.getTime() + minutes * 60_000);

  const hours: number[] | null = Array.isArray(o.byhour) && o.byhour.length ? o.byhour : null;
  if (!hours) return;   // no BYHOUR: the time comes from DTSTART, which already moved
  const mins: number[] = Array.isArray(o.byminute) && o.byminute.length ? o.byminute : [0];

  let dayShift = 0;
  const nextHours = new Set<number>();
  const nextMinutes = new Set<number>();
  for (const h of hours) {
    for (const m of mins) {
      const total = h * 60 + m + minutes;
      dayShift = Math.floor(total / 1440);
      const within = ((total % 1440) + 1440) % 1440;
      nextHours.add(Math.floor(within / 60));
      nextMinutes.add(within % 60);
    }
  }
  o.byhour = [...nextHours].sort((a, b) => a - b);
  if (Array.isArray(o.byminute) && o.byminute.length) o.byminute = [...nextMinutes].sort((a, b) => a - b);

  if (dayShift !== 0) {
    const wrapDay = (d: number) => (((d + dayShift) % 7) + 7) % 7;
    if (Array.isArray(o.byweekday)) o.byweekday = o.byweekday.map(wrapDay).sort((a: number, b: number) => a - b);
    if (Array.isArray(o.bynweekday)) o.bynweekday = o.bynweekday.map(([d, n]: [number, number]) => [wrapDay(d), n]);
    if (Array.isArray(o.bymonthday) && o.bymonthday.every((d: number) => d + dayShift >= 1 && d + dayShift <= 28)) {
      o.bymonthday = o.bymonthday.map((d: number) => d + dayShift);
    }
  }
};

/** A rule instant from one anchor's time base, re-expressed in another's. */
const rebaseInstant = (instant: Date, fromAnchor: Date, toAnchor: Date): Date =>
  toRuleTimeBase(fromRuleTimeBase(instant, fromAnchor), toAnchor);

/**
 * Refuse to rewrite a rule that has no anchor.
 *
 * `rrulestr` does not fail on a rruleset with no DTSTART — it quietly substitutes
 * "now". Every write path here then treats the moment the user happened to drag as
 * the series' start, and the rule that gets SAVED comes back stamped with the wall
 * clock of the drag (BYHOUR/BYMINUTE/BYSECOND of whenever it happened), or loses
 * its recurrence altogether and turns a repeating message into a single one. A
 * display artefact becomes a real schedule nobody chose, and it is the write that
 * makes it permanent.
 *
 * Throwing is the safe answer: the calendar's drop handlers already treat a throw
 * as "that did nothing", so the customer's data is left exactly as it was rather
 * than replaced with a guess.
 */
const requireRuleAnchor = (rruleString: string): void => {
  if (!/(^|\n)DTSTART(;[^:\n]*)?:/i.test(rruleString ?? '')) {
    throw new Error(
      'Refusing to rewrite a recurrence rule with no DTSTART anchor: the rule would be ' +
      'rebuilt around the current time instead of the series it belongs to.',
    );
  }
};

/**
 * The first instant `set` still emits at or after `from`, honouring EXDATEs.
 *
 * A pending recurring document's `dueDate` is its NEXT UNSENT occurrence: the
 * backend sends from it, and the calendar hides everything before it. So once an
 * occurrence is excluded, any `dueDate` still pointing at it is a lie in both
 * directions — the server fires a send the customer cancelled, and the calendar
 * measures visibility against an instant that no longer exists. Whenever an
 * EXDATE is written, the dueDate has to be re-seated onto something the rule
 * actually emits.
 *
 * Returns null when the series has nothing left, which is the caller's signal
 * that the document should go rather than be patched.
 */
/**
 * Rebuild a rule from its own options WITHOUT losing "the nth weekday of the month".
 *
 * Every write path here reconstructs the series as `new RRule(rule.options)` before
 * editing it. For `FREQ=MONTHLY;BYDAY=+2MO` that reconstruction is lossy in a way
 * that is easy to miss: the parsed rule keeps the constraint in `options.bynweekday`
 * as `[[0, 2]]`, and the rebuilt rule has BOTH `byweekday` and `bynweekday` null,
 * with the pair surviving only in `origOptions` — where `toString()` finds it and
 * emits `BYNWEEKDAY=0,2`, a property RRULE does not have and rrule's own parser
 * rejects.
 *
 * So the rebuilt series was already the WRONG one — a monthly rule with no day
 * constraint falls back to the day of the month DTSTART lands on, turning "every
 * second Monday" into "the 10th of every month" — and any attempt to persist it
 * threw, which the calendar showed as a drag that simply did nothing.
 *
 * Passing the constraint back through `byweekday` as `Weekday` instances is the form
 * rrule both honours and serialises correctly, as `BYDAY=+2MO`.
 */
const rebuildRule = (rule: RRule, overrides: Record<string, unknown> = {}): RRule => {
  const options: any = { ...rule.options, ...overrides };
  // A caller that supplies `byweekday` has already decided the day constraint
  // (e.g. retargeted an nth weekday to where the series moved); re-appending the
  // rule's own nth weekdays on top would give the series both days.
  const nth = 'byweekday' in overrides
    ? null
    : (rule.options as any).bynweekday ?? (rule.origOptions as any)?.bynweekday;
  if ('byweekday' in overrides) delete options.bynweekday;

  if (Array.isArray(nth) && nth.length) {
    const asWeekdays = nth
      .map((entry: any) => {
        if (Array.isArray(entry) && entry.length >= 2) return new Weekday(entry[0], entry[1]);
        if (entry && typeof entry === 'object' && typeof entry.weekday === 'number') {
          return new Weekday(entry.weekday, entry.n ?? undefined);
        }
        return null;
      })
      .filter((w: Weekday | null): w is Weekday => w !== null);

    if (asWeekdays.length) {
      const plain = Array.isArray(options.byweekday) ? options.byweekday : [];
      options.byweekday = [...plain, ...asWeekdays];
      delete options.bynweekday;
    }
  }

  return new RRule(options);
};

/**
 * The rule's "nth weekday of the month" constraints (`BYDAY=+2MO` -> Weekday(MO, 2)),
 * wherever rrule happens to be keeping them: `options.bynweekday` on a parsed rule,
 * only `origOptions` on one rebuilt with `new RRule(options)`.
 */
const nthWeekdaysOf = (rule: RRule): Weekday[] => {
  const raw = (rule.options as any).bynweekday ?? (rule.origOptions as any)?.bynweekday;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry: any) => {
      if (Array.isArray(entry) && entry.length >= 2) return new Weekday(entry[0], entry[1]);
      if (entry && typeof entry === 'object' && typeof entry.weekday === 'number' && entry.n) {
        return new Weekday(entry.weekday, entry.n);
      }
      return null;
    })
    .filter((w: Weekday | null): w is Weekday => w !== null);
};

/** rrule's weekday index (MO=0 .. SU=6) of an instant, read in UTC as rrule does. */
const rruleWeekdayOf = (d: Date): number => (d.getUTCDay() === 0 ? 6 : d.getUTCDay() - 1);

/**
 * Move an nth-weekday constraint so it describes where the series now lands.
 *
 * The entry for the weekday the occurrence was dragged FROM is replaced by the
 * weekday it landed ON, with the ordinal that day actually has in its month
 * (counted from the end when the original was, like `-1FR` "last Friday"). So
 * "second Monday" dragged one day later becomes "second Tuesday" -- the drip
 * drags' convention -- and a drag of whole weeks changes the ordinal instead,
 * which is the only way the tail's first send can be the one the customer placed.
 * Entries for other weekdays are left alone.
 */
const retargetNthWeekdays = (nth: Weekday[], from: Date, to: Date): Weekday[] => {
  const fromDay = rruleWeekdayOf(from);
  const toDay = rruleWeekdayOf(to);
  const dayOfMonth = to.getUTCDate();
  const daysInMonth = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() + 1, 0)).getUTCDate();
  const fromStart = Math.ceil(dayOfMonth / 7);
  const fromEnd = -(Math.floor((daysInMonth - dayOfMonth) / 7) + 1);
  let replaced = false;
  return nth.map(w => {
    if (replaced || w.weekday !== fromDay) return w;
    replaced = true;
    return new Weekday(toDay, (w.n ?? 1) < 0 ? fromEnd : fromStart);
  });
};

/**
 * A split's tail, bounded by the customer's END DATE and by the sends the series
 * had left from the split point.
 *
 * Ruling (2026-09-11): moving one occurrence "and all the ones after it" is not a
 * request to run past the end date the customer chose. The tail used to carry
 * `offsetRule`'s SHIFTED UNTIL, so a monthly-10th series ending Aug 10, dragged 2
 * days later, ended Aug 12. Holding the end date alone is not enough either: a
 * tail moved EARLIER can then fit one more send before it than the series ever
 * had. So the tail ends at whichever comes first -- the customer's end, or its
 * own `sendsLeft`-th send. The one thing this can cost is a send a LATER move
 * pushes past the end date, which is exactly what honouring the end date means.
 *
 * Moving the WHOLE series (from its first occurrence, or "all") is a different
 * request and still carries the end date with it, as drip drags do.
 */
const boundTailToEndDate = (tail: RRule, customerEnd: Date, sendsLeft: number): RRule => {
    const clipped = rebuildRule(tail, { until: new Date(customerEnd.getTime()), count: undefined });
    if (sendsLeft < 1) return clipped;
    // Bounded by UNTIL, so this terminates; collect one past the allowance.
    const fitting = clipped.all((_d, i) => i <= sendsLeft);
    return fitting.length > sendsLeft
        ? rebuildRule(tail, { until: new Date(fitting[sendsLeft - 1].getTime()), count: undefined })
        : clipped;
};

const nextEmittedOnOrAfter = (set: RRuleSet, from: Date): Date | null => {
    // inc = true so an unexcluded `from` resolves to itself and the dueDate stays put.
    return set.after(from, true) ?? null;
};

/** The `RRULE:` property line of a ruleset string, or '' -- the schedule's SHAPE,
 *  without the DTSTART anchor or EXDATEs. */
const ruleLineOf = (rruleString: string | undefined): string =>
    (rruleString ?? '').split(/\r?\n/).map(l => l.trim()).find(l => /^RRULE:/i.test(l)) ?? '';

/** Same minute, the precision every chip, dueDate and DTSTART here is kept at. */
const sameMinute = (a: Date, b: Date): boolean =>
    Math.floor(a.getTime() / 60_000) === Math.floor(b.getTime() / 60_000);

/**
 * A series re-anchored so its FIRST emitted instant is `anchor`.
 *
 * The edit path's tail ("this one and all the ones after it") is built from the
 * compose DRAFT's rule, and for a content-only edit that draft is the stored
 * series rule verbatim -- its DTSTART is still the series' ORIGINAL start. Giving
 * that rule the remaining COUNT without moving DTSTART made it end exactly where
 * the head already ends (COUNT=3 from Jan 4 is Jan 4/11/18), so `validateRRule`
 * found nothing after the tail's first send and the tail became a one-off: the
 * customer's last two sends vanished. The drag path never had this, because it
 * re-anchors through `offsetRule`; this is the same re-anchoring, built with
 * `rebuildRule` so the constraints `offsetRule` discards (the nth weekday of a
 * month, BYMONTHDAY, BYSETPOS) survive.
 *
 * `count` replaces the rule's COUNT when given -- the sends the series has LEFT,
 * never the draft's own COUNT, which the date/time pickers copy from the stored
 * TOTAL. An UNTIL is kept as-is: the tail keeps the same end date.
 */
const reanchorRule = (
    rule: RRule,
    anchor: Date,
    replacingWeekdayOf: Date,
    count: number | undefined,
): RRule => {
    const overrides: Record<string, unknown> = { dtstart: new Date(anchor.getTime()) };
    if (count !== undefined) overrides.count = count;

    const freq = rule.options.freq;
    if (freq !== Frequency.MINUTELY) {
        if (freq !== Frequency.HOURLY) overrides.byhour = [anchor.getUTCHours()];
        overrides.byminute = [anchor.getUTCMinutes()];
    }
    overrides.bysecond = [anchor.getUTCSeconds()];

    // A weekly series moved to another weekday takes the new weekday in place of
    // the old one, as a drag does; otherwise its first emitted instant would not
    // be the occurrence the customer placed.
    const plainWeekdays = rule.options.byweekday;
    if (freq === Frequency.WEEKLY && Array.isArray(plainWeekdays) && plainWeekdays.length) {
        const toRRuleDay = (d: Date) => (d.getUTCDay() === 0 ? 6 : d.getUTCDay() - 1);
        const from = toRRuleDay(replacingWeekdayOf);
        const to = toRRuleDay(anchor);
        if (!plainWeekdays.includes(to)) {
            const i = plainWeekdays.indexOf(from);
            if (i !== -1) {
                const next = [...plainWeekdays];
                next[i] = to;
                overrides.byweekday = next.sort((a, b) => a - b);
            }
        }
    }

    // A monthly/yearly "nth weekday" series likewise follows the move.
    const nth = nthWeekdaysOf(rule);
    if (nth.length && (freq === Frequency.MONTHLY || freq === Frequency.YEARLY)) {
        const plain = Array.isArray(plainWeekdays) ? plainWeekdays : [];
        overrides.byweekday = [...plain, ...retargetNthWeekdays(nth, replacingWeekdayOf, anchor)];
    }

    // And a "day N of the month" series (BYMONTHDAY, which the recurrence popover
    // writes for monthly rules): the day the occurrence was moved FROM becomes the
    // day it landed ON. Kept as-is, a series edited from the 10th to the 12th
    // skipped straight to the NEXT 10th -- the occurrence the customer placed
    // never sent.
    const monthDays = rule.options.bymonthday;
    if (Array.isArray(monthDays) && monthDays.length) {
        const i = monthDays.indexOf(replacingWeekdayOf.getUTCDate());
        if (i !== -1 && !monthDays.includes(anchor.getUTCDate())) {
            const next = [...monthDays];
            next[i] = anchor.getUTCDate();
            overrides.bymonthday = next.sort((a, b) => a - b);
        }
    }

    return rebuildRule(rule, overrides);
};

/**
 * A result computed in the FLOATING frame -> the real world: true-instant dueDates
 * and a TZID rule string. Inverse of the entry normalisation in the wrappers below.
 */
const denormalizeTzResult = (r: RecurrenceHandlerResult, tzid: string): RecurrenceHandlerResult => ({
  ...r,
  originalDueDate: r.originalDueDate ? floatingIsoToTrueIso(r.originalDueDate, tzid) : r.originalDueDate,
  newDueDate: r.newDueDate ? floatingIsoToTrueIso(r.newDueDate, tzid) : r.newDueDate,
  // DTSTART/EXDATE back to TZID form, AND the RRULE-line UNTIL (written floating during
  // the edit) back to a TRUE UTC instant — else the engine duplicates/keeps the boundary send.
  originalNewRRule: untilFloatingToTrue(addTzidToRuleString(r.originalNewRRule, tzid), tzid),
  newRRule: untilFloatingToTrue(addTzidToRuleString(r.newRRule, tzid), tzid),
});

export class RRuleRecurrenceHandler {
  private static nullableKeys = [
    'until', 'tzid', 'bysetpos', 'bymonth', 'byyearday',
    'byweekno', 'bynweekday', 'byeaster', 'count'
  ];

  private static getWeekday(date: Date): number {
    const jsDay = date.getUTCDay();
    // In RRule, MO=0, TU=1, WE=2, TH=3, FR=4, SA=5, SU=6
    // In JavaScript, SU=0, MO=1, TU=2, WE=3, TH=4, FR=5, SA=6
    // So we need to convert from JavaScript to RRule
    return jsDay === 0 ? 6 : jsDay - 1;
  }

  private static cleanRuleOptions(options: any): any {
    const cleanedOptions = { ...options };

    // Delete any keys that are null, undefined, or empty arrays
    this.nullableKeys.forEach(key => {
      if (cleanedOptions[key] == null ||
        (Array.isArray(cleanedOptions[key]) && cleanedOptions[key].length === 0)) {
        delete cleanedOptions[key];
      }
    });

    // `bynweekday` is rrule's INTERNAL spelling of "the nth weekday of the month"
    // (`BYDAY=+2MO` parses to `[[0, 2]]`). Emitting it verbatim produces
    // `BYNWEEKDAY=0,2`, which is not an RRULE property and which rrule's own parser
    // rejects — so every write path, which re-parses what it just wrote, threw, and
    // the calendar turned that into a silent refusal to move.
    //
    // Deleting it — what this used to do — is worse than throwing: a MONTHLY rule
    // with no day constraint falls back to the day of the month DTSTART lands on, so
    // "every second Monday" silently becomes "the 10th of every month" and that
    // invented schedule gets written to the customer's data. Convert it instead into
    // the `byweekday` form that serialises as a real `BYDAY=+2MO` token.
    // Two shapes reach here, because the handler both PARSES rules and REBUILDS them
    // with `new RRule(options)`: a freshly parsed rule carries `[[weekday, nth]]`
    // pairs, a rebuilt one carries `Weekday`-like objects. Handling only the first
    // silently dropped the constraint on every rebuilt rule, which is the very
    // "second Monday quietly becomes the 10th" outcome this exists to prevent.
    if (Array.isArray(cleanedOptions.bynweekday) && cleanedOptions.bynweekday.length) {
      const nthWeekdays = cleanedOptions.bynweekday
        .map((entry: any) => {
          if (Array.isArray(entry) && entry.length >= 2) return new Weekday(entry[0], entry[1]);
          if (entry && typeof entry === 'object' && typeof entry.weekday === 'number') {
            return new Weekday(entry.weekday, entry.n ?? undefined);
          }
          return null;
        })
        .filter((w: Weekday | null): w is Weekday => w !== null);

      const plainWeekdays = Array.isArray(cleanedOptions.byweekday) ? cleanedOptions.byweekday : [];
      if (nthWeekdays.length) cleanedOptions.byweekday = [...plainWeekdays, ...nthWeekdays];
    }
    delete cleanedOptions.bynweekday;

    return cleanedOptions;
  }

  private static formatRuleSetToString(rules: RRule[], exdates: Date[]): string {
    if (!rules.length) return '';

    const rule = rules[0];

    const options = this.cleanRuleOptions(rule.options);

    // Get the RRULE string without DTSTART
    const rruleOptions = { ...options };
    delete rruleOptions.dtstart;  // Remove dtstart from RRULE part
    const rruleString = new RRule(rruleOptions).toString();

    // Build the complete string
    let result = '';

    // Add DTSTART if it exists
    if (options.dtstart) {
      const dtstart = new Date(Date.UTC(
        options.dtstart.getUTCFullYear(),
        options.dtstart.getUTCMonth(),
        options.dtstart.getUTCDate(),
        options.dtstart.getUTCHours(),
        options.dtstart.getUTCMinutes(),
        options.dtstart.getUTCSeconds()
      ));
      result += `DTSTART:${dtstart.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z')}\n`;
    }

    // Add RRULE (will not include DTSTART now)
    result += `RRULE:${rruleString.replace(/^RRULE:/, '')}`;

    // Add EXDATE if there are any
    if (exdates.length > 0) {
      const formattedExdates = exdates.map(date => {
        const utcDate = new Date(Date.UTC(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate(),
          date.getUTCHours(),
          date.getUTCMinutes(),
          date.getUTCSeconds()
        ));
        const formatted = utcDate.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
        return formatted;
      });
      result += `\nEXDATE:${formattedExdates.join(',')}`;
    } else {
      console.log('[EXDATE_DEBUG] formatRuleSetToString - No exdates to add');
    }

    return result;
  }

  private static offsetRule(rule: RRule, draggedDate: Date, newDate: Date, scope: 'this' | 'thisAndFollowing' | 'all'): RRule {

    // Calculate the time difference for the offset
    const timeDiff = newDate.getTime() - draggedDate.getTime();

    // Create new start time with offset
    const newStartTime = new Date(draggedDate.getTime());
    newStartTime.setTime(newDate.getTime());

    // Create a fresh options object with only the essential frequency properties
    const options: any = {
      freq: rule.options.freq,
      interval: rule.options.interval || 1,
      wkst: rule.options.wkst || 0,
      dtstart: newStartTime
    };

    // CRITICAL FIX: Preserve COUNT from original rule
    if (rule.options.count !== undefined) {
      options.count = rule.options.count;
    }

    // Special handling for weekly frequency - DO NOT MODIFY THIS BLOCK
    if (rule.options.freq === Frequency.WEEKLY) {
      const draggedDay = RRuleRecurrenceHandler.getWeekday(draggedDate);
      const newDay = RRuleRecurrenceHandler.getWeekday(newDate);

      if (scope === 'thisAndFollowing') {
        // For thisAndFollowing, replace the dragged weekday with the new weekday
        if (rule.options.byweekday) {
          const updatedWeekdays = [...rule.options.byweekday];
          const draggedDayIndex = updatedWeekdays.indexOf(draggedDay);

          if (draggedDayIndex !== -1) {
            // Replace the dragged weekday with the new weekday
            updatedWeekdays[draggedDayIndex] = newDay;
            options.byweekday = updatedWeekdays.sort((a: number, b: number) => a - b);
          } else {
            options.byweekday = rule.options.byweekday;
          }
        } else {
          options.byweekday = [newDay];
        }
      } else if (rule.options.byweekday) {
        // For other scopes, keep existing behavior
        const updatedWeekdays = [...rule.options.byweekday];
        const draggedDayIndex = updatedWeekdays.indexOf(draggedDay);

        if (draggedDayIndex !== -1) {
          updatedWeekdays[draggedDayIndex] = newDay;
          options.byweekday = updatedWeekdays.sort((a: number, b: number) => a - b);
        } else {
          options.byweekday = rule.options.byweekday;
        }
      } else {
        options.byweekday = [newDay];
      }
    }

    // A monthly/yearly "nth weekday" series (`BYDAY=+2MO`). The fresh options above
    // carry no day constraint at all, so this used to fall back to the day of the
    // month the new start lands on: "second Monday" dragged to the 13th became
    // "the 13th of every month".
    const nth = nthWeekdaysOf(rule);
    if (nth.length && (rule.options.freq === Frequency.MONTHLY || rule.options.freq === Frequency.YEARLY)) {
      options.byweekday = retargetNthWeekdays(nth, draggedDate, newDate);
    }

    // Handle until if it exists by applying the same time offset
    if (rule.options.until) {
      // Create a new UNTIL date that preserves the original date but uses the time from newDate
      const originalUntil = rule.options.until;
      
      // Fix: Shift the until date by the full time difference to keep the same duration/window
      const newUntil = new Date(originalUntil.getTime() + timeDiff);
      
      // Use this adjusted UNTIL for the new rule
      options.until = newUntil;
    }

    // For minutely/hourly frequencies, don't restrict their respective time components
    // For minutely, don't restrict hours either as minutes should flow across hours
    if (rule.options.freq !== Frequency.MINUTELY) {
      if (rule.options.freq !== Frequency.HOURLY) {
        options.byhour = [newDate.getUTCHours()];
      }
      options.byminute = [newDate.getUTCMinutes()];
    }

    // Always set seconds
    options.bysecond = [newDate.getUTCSeconds()];

    // Clean up the options and create a new rule
    const cleanedOptions = RRuleRecurrenceHandler.cleanRuleOptions(options);

    const finalRule = new RRule(cleanedOptions);

    return finalRule;
  }

  private formatDate(date: Date, includeTime: boolean = true, includeTimezone: boolean = true): string {
    return date.toISOString();
  }

  private static calculateRemainingCount(rule: RRule, draggedDate: Date): number | undefined {

    // If no count was set, return undefined to maintain infinite recurrence
    const count = rule.options.count;
    if (!count) {
      return undefined;
    }

    // Create a rule to get all events up to but not including the dragged date.
    // Through `rebuildRule`: a bare `new RRule(options)` drops an nth weekday and
    // counts a different schedule.
    const beforeRule = rebuildRule(rule, {
      until: new Date(draggedDate.getTime() - 1) // Subtract 1ms to exclude the dragged date
    });

    // Get count of events before the dragged date (not including it)
    const eventsBefore = beforeRule.all().length;

    // Calculate remaining events (including the dragged event)
    const remainingCount = Math.max(1, count - eventsBefore);

    return remainingCount;
  }

  private static validateRRule(rruleStr: string, referenceDate: Date): string | undefined {
    if (!rruleStr) return undefined;
    try {
      // Parse using forceset so we work with RRuleSet, then check the first rule.
      const ruleSet = rrulestr(rruleStr, { forceset: true }) as RRuleSet;
      const mainRule = ruleSet.rrules()[0];
      // See if there's an occurrence after the referenceDate.
      const nextOccurrence = mainRule.after(referenceDate, false);
      // If there is no next occurrence, then the rule doesn't really recur.
      if (!nextOccurrence) {
        return undefined;
      }
      return rruleStr;
    } catch (e) {
      return rruleStr; // fallback if there's a parsing issue
    }
  }

  // BE#648: a MIGRATED (TZID) series is rewritten in the FLOATING frame — rule and
  // all input instants normalised to wall-clock, the browser-offset time-base math
  // neutralised, and the result denormalised back to true instants + a TZID rule.
  // Legacy (no-TZID) series go straight to the impl, byte-identical to before.
  public handleDragAndDrop(
    ruleString: string,
    displayedDraggedDate: Date,
    newDate: Date,
    scope: 'this' | 'thisAndFollowing' | 'all',
    originalDateTime: Date
  ): RecurrenceHandlerResult {
    const tzid = tzidOfRuleString(ruleString);
    if (!tzid) {
      return this.handleDragAndDropImpl(ruleString, displayedDraggedDate, newDate, scope, originalDateTime);
    }
    FLOATING_TIME_BASE = true;
    try {
      const result = this.handleDragAndDropImpl(
        toFloatingRuleString(ruleString),
        instantToFloating(displayedDraggedDate, tzid),
        instantToFloating(newDate, tzid),
        scope,
        instantToFloating(originalDateTime, tzid),
      );
      return denormalizeTzResult(result, tzid);
    } finally {
      FLOATING_TIME_BASE = false;
    }
  }

  private handleDragAndDropImpl(
    ruleString: string,
    displayedDraggedDate: Date,
    newDate: Date,
    scope: 'this' | 'thisAndFollowing' | 'all',
    originalDateTime: Date
  ): RecurrenceHandlerResult {

    requireRuleAnchor(ruleString);
    let ruleSet = rrulestr(ruleString, { forceset: true }) as RRuleSet;
    const mainRule = ruleSet.rrules()[0];

    // TWO TIME BASES, NAMED ONCE, SO NOTHING HAS TO GUESS.
    //
    // `originalDateTime` is the document's stored `dueDate`, and a stored dueDate
    // is in WALL-CLOCK terms: the backend rolls it forward with
    // `calculateNextDuedate`, which returns `adjustForDSTChange(...)`. So once the
    // next send is on the far side of a clock change it no longer coincides with
    // the instant the rule emits. It used to double as the rule's own offset
    // reference, which meant that for exactly those series the chip and the
    // reference agreed and the correction switched itself off: the EXDATE landed
    // an hour off every emitted instant and excluded nothing, while the re-seated
    // DTSTART landed BETWEEN occurrences and dropped the current one.
    //
    // The rule's time base belongs to the rule, so take it from the rule: DTSTART,
    // captured before anything re-seats it. Everything that reaches INTO the rule
    // (the chip, the DTSTART rebase, occurrence lookups, `validateRRule`'s
    // reference) speaks `originalRuleTime`; everything handed BACK as a dueDate
    // comes home through `fromRuleTimeBase`.
    //
    // `newDate` is deliberately left alone — it is a real instant the user picked,
    // and becomes a due date or a new DTSTART rather than a lookup into this rule.
    const originalAnchor = new Date(mainRule.options.dtstart.getTime());

    // Re-seat the rule onto the document's current position so it speaks the right
    // time-of-day — spending the COUNT the series has already used, rather than
    // re-arming the whole budget from the later start. The rebase may MOVE the
    // rule's time base (see rebaseDtstartConservingCount), so every rule-time value
    // below is computed against the anchor it leaves behind, not the original one.
    rebaseDtstartConservingCount(mainRule, toRuleTimeBase(originalDateTime, originalAnchor));
    const ruleAnchor = new Date(mainRule.options.dtstart.getTime());
    const draggedDate = toRuleTimeBase(displayedDraggedDate, ruleAnchor);
    const originalRuleTime = toRuleTimeBase(originalDateTime, ruleAnchor);

    // Fix: Reconstruct the ruleset with the updated rule. Existing EXDATEs were
    // written in the ORIGINAL base; re-express them in the new one, or they stop
    // coinciding with anything and the occurrences they excluded come back.
    const fixedRuleSet = new RRuleSet();
    fixedRuleSet.rrule(rebuildRule(mainRule));
    ruleSet.exdates().forEach(exdate => fixedRuleSet.exdate(rebaseInstant(exdate, originalAnchor, ruleAnchor)));
    ruleSet = fixedRuleSet;

    // Use the explicitly provided originalDateTime
    const originalDueDate = this.formatDate(originalDateTime, true, true);

    // Helper function to check if a date is the first occurrence
    const isFirstOccurrence = (date: Date): boolean => {
      const startDate = mainRule.options.dtstart;
      const endDate = new Date(startDate.getTime());
      endDate.setDate(endDate.getDate() + 1);

      const firstOccurrence = mainRule.between(startDate, endDate, true)[0];
      return firstOccurrence &&
        firstOccurrence.getTime() === startDate.getTime() &&
        date.getTime() === startDate.getTime();
    };

    if (scope === 'this') {
      // Create new rule set for original series
      const originalSet = new RRuleSet();
      originalSet.rrule(rebuildRule(mainRule));

      // Copy existing exclusions
      const existingExdates = ruleSet.exdates();
      existingExdates.forEach(existingExdate => {
        originalSet.exdate(existingExdate);
      });

      // Add the dragged date as an exclusion
      originalSet.exdate(new Date(draggedDate.getTime()));

      // Re-seat the document's dueDate onto an occurrence the rule still emits.
      // It used to be reset to DTSTART, which had two failure modes: dragging the
      // occurrence the dueDate pointed at left the document due at its own EXDATE
      // (so the backend sent the occurrence the customer had just moved away), and
      // on a series whose chain had already advanced it rewound the dueDate far
      // enough that already-sent occurrences reappeared on the calendar.
      const remainingDue = nextEmittedOnOrAfter(originalSet, originalRuleTime);

      if (!remainingDue) {
        // The moved occurrence was the only one the series had left. The message
        // lives on as the newly created one at the dropped date, so keeping an
        // empty husk behind would draw a chip for an occurrence that can never fire.
        return {
          originalAction: 'delete',
          originalDueDate: this.formatDate(originalDateTime, true, true),
          newAction: 'create',
          newDueDate: this.formatDate(newDate, true, true)
        };
      }

      // Format and validate the original series with the new exclusion
      const originalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(originalSet.rrules(), originalSet.exdates());
      // `remainingDue` came out of the rule, so it is a rule instant: it goes back
      // to wall clock to be STORED, and stays in rule time as `validateRRule`'s
      // reference (which walks the rule with `.after`). Reading the stored value
      // back for that check is what makes a converted dueDate turn a live series
      // into a one-off.
      const originalDueDate = this.formatDate(fromRuleTimeBase(remainingDue, ruleAnchor), true, true);
      const validatedOriginalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(originalNewRRule, remainingDue);

      return {
        originalAction: 'patch',
        originalDueDate: originalDueDate,
        originalNewRRule: validatedOriginalNewRRule,
        newAction: 'create',
        newDueDate: this.formatDate(newDate, true, true)
      };
    } else if (scope === 'thisAndFollowing') {
      if (isFirstOccurrence(draggedDate)) {
        // The first occurrence has nothing before it, so "this and all the ones after
        // it" covers the entire series: there is no HEAD to keep and no TAIL to mint.
        // The operation is RE-ANCHORING, and the series already has a document to
        // re-anchor — so patch it in place rather than deleting it and building a
        // replacement.
        //
        // Delete+create here was not a soft delete. It 404'd the original `_id`, so
        // every reference to it dangled; the replacement inherited the original's
        // `createdAt`; and it was minted through the split-occurrence path, which
        // derives an OCCURRENCE-scoped `:occ:<from>:to:<to>` idempotency key — the key
        // the send path dedups on — and hung it on a whole recurring series. Worst of
        // all, delete+create has a state where the create failed and the message no
        // longer exists anywhere. `handleRecurringEdit`'s `all` scope was moved off
        // this same pattern for these same reasons (FE#151 follow-up); this is that
        // case wearing a different scope.
        const newRule = (this.constructor as typeof RRuleRecurrenceHandler).offsetRule(mainRule, draggedDate, newDate, scope);
        const newSet = new RRuleSet();
        newSet.rrule(newRule);

        // Handle EXDATEs for the new series
        // CRITICAL FIX: When moving entire series (delete+create), carry forward ALL EXDATEs with offset
        // Don't filter by draggedDate - all exclusions should be preserved in the moved series
        const timeDiff = newDate.getTime() - draggedDate.getTime();
        const offsetEXDATEs = ruleSet.exdates()
          .map(d => new Date(d.getTime() + timeDiff));
        offsetEXDATEs.forEach(d => newSet.exdate(d));

        const newRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(newSet.rrules(), newSet.exdates());
        const newDueDate = this.formatDate(newDate, true, true);
        const validatedNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(newRRule, new Date(newDueDate));

        return {
          originalAction: 'patch',
          originalDueDate: newDueDate,
          originalNewRRule: validatedNewRRule,
          newAction: 'none'
        };
      }

      // For weekly recurrence, check if it's the first occurrence of the weekday
      if (mainRule.options.freq === Frequency.WEEKLY) {
        const draggedDay = RRuleRecurrenceHandler.getWeekday(draggedDate);
        const newDay = RRuleRecurrenceHandler.getWeekday(newDate);

        // Find the first occurrence of this weekday in the series
        const firstOccurrenceOfDay = mainRule.between(
          mainRule.options.dtstart,
          draggedDate,
          true
        ).find(date => RRuleRecurrenceHandler.getWeekday(date) === draggedDay);

        const isFirstOfDay = firstOccurrenceOfDay &&
          firstOccurrenceOfDay.getTime() === draggedDate.getTime();

        if (isFirstOfDay) {
          // If it's the first occurrence of this weekday, just update the weekday in the original series
          const updatedOptions = { ...mainRule.options };
          if (updatedOptions.byweekday) {
            const updatedWeekdays = [...updatedOptions.byweekday];
            const draggedDayIndex = updatedWeekdays.indexOf(draggedDay);
            if (draggedDayIndex !== -1) {
              updatedWeekdays[draggedDayIndex] = newDay;
              updatedOptions.byweekday = updatedWeekdays.sort((a: number, b: number) => a - b);
            }
          } else {
            updatedOptions.byweekday = [newDay];
          }

          const updatedSet = new RRuleSet();
          updatedSet.rrule(rebuildRule(mainRule, { byweekday: updatedOptions.byweekday }));

          // Copy all existing EXDATEs
          ruleSet.exdates().forEach(d => updatedSet.exdate(d));

          const updatedRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(updatedSet.rrules(), updatedSet.exdates());
          const originalDueDate = this.formatDate(fromRuleTimeBase(mainRule.options.dtstart, ruleAnchor), true, true);
          const validatedUpdatedRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(updatedRRule, mainRule.options.dtstart);

          return {
            originalAction: 'patch',
            originalDueDate: originalDueDate,
            originalNewRRule: validatedUpdatedRRule,
            newAction: 'none'
          };
        }
      }

      // If not first occurrence or not weekly, proceed with splitting the series
      // Create truncated rule for original series (omit count — we use until instead)
      const untilDate = new Date(draggedDate.getTime());
      untilDate.setMinutes(untilDate.getMinutes() - 1); // End one minute before dragged date
      const truncatedOptions = { ...mainRule.options, count: undefined, until: untilDate };

      // Every rebuild below goes through `rebuildRule`. `new RRule(parsed.options)`
      // keeps an nth weekday only in `origOptions`: the rule then EXPANDS a different
      // schedule and serialises as `BYNWEEKDAY=0,2`, which rrule's own parser rejects
      // -- a "second Monday" drag threw here and the chip silently snapped back.
      let truncatedSet = new RRuleSet();
      truncatedSet.rrule(rebuildRule(mainRule, { count: undefined, until: untilDate }));

      // Copy EXDATEs that occur before dragged date
      const exdatesBefore = ruleSet.exdates()
        .filter(d => d < draggedDate) // Only include exdates before dragged date
        .map(d => new Date(d.getTime()));
      exdatesBefore.forEach(d => truncatedSet.exdate(d));

      // CRITICAL FIX: Check if truncated series would have any valid occurrences
      // This happens when the first occurrence is deleted and we drag the second occurrence
      const firstValidOccurrence = truncatedSet.after(new Date(truncatedOptions.dtstart.getTime() - 1000), false);
      if (!firstValidOccurrence) {
        console.log('[DRAG_AND_DROP] Truncated series would have no valid occurrences, treating as delete+create');
        
        // Handle this as delete original + create new series (same as first occurrence logic)
        const newRule = (this.constructor as typeof RRuleRecurrenceHandler).offsetRule(mainRule, draggedDate, newDate, scope);
        const newSet = new RRuleSet();
        newSet.rrule(newRule);

        // Handle EXDATEs for the new series
        const timeDiff = newDate.getTime() - draggedDate.getTime();
        const exdatesAfter = ruleSet.exdates()
          .filter(d => d >= draggedDate)
          .map(d => new Date(d.getTime() + timeDiff));
        exdatesAfter.forEach(d => newSet.exdate(d));

        const newRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(newSet.rrules(), newSet.exdates());
        const newDueDate = this.formatDate(newDate, true, true);
        const validatedNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(newRRule, new Date(newDueDate));

        return {
          originalAction: 'delete',
          originalDueDate: this.formatDate(fromRuleTimeBase(mainRule.options.dtstart, ruleAnchor), true, true),
          newAction: 'create',
          newDueDate: newDueDate,
          newRRule: validatedNewRRule
        };
      }

      // Create new series starting from new date
      const newRule = (this.constructor as typeof RRuleRecurrenceHandler).offsetRule(mainRule, draggedDate, newDate, scope);

      // Calculate remaining count for the new series if original had count
      let remainingCount: number | undefined = undefined;
      if (mainRule.options.count) {
        // Count events in original pattern up to (but not including) dragged date
        const untilForCount = new Date(draggedDate.getTime());
        untilForCount.setMinutes(untilForCount.getMinutes() - 1); // End one minute before dragged date
        const originalRuleWithUntil = rebuildRule(mainRule, {
          until: untilForCount,
          count: undefined     // Remove count since we're using until
        });
        const eventsBeforeDraggedDate = originalRuleWithUntil.all().length;
        remainingCount = Math.max(1, Number(mainRule.options.count) - eventsBeforeDraggedDate);
      }

      const newSet = new RRuleSet();
      if (remainingCount !== undefined) {
        const originalRuleCount = Number(mainRule.options.count) - remainingCount;

        // CRITICAL FIX: Preserve EXDATEs when recreating truncatedSet for COUNT distribution
        const preservedExdates = truncatedSet.exdates().map(d => new Date(d.getTime()));
        
        truncatedSet = new RRuleSet();
        truncatedSet.rrule(rebuildRule(mainRule, { until: undefined, count: originalRuleCount }));
        
        // Re-add the preserved EXDATEs
        preservedExdates.forEach(d => truncatedSet.exdate(d));

        newSet.rrule(rebuildRule(newRule, { count: remainingCount }));
      } else if (mainRule.options.until && !remainingCount) {
        // The tail keeps the customer's END DATE, and gains no send the series did
        // not have (see boundTailToEndDate for the ruling and why both halves are
        // needed). This used to keep `offsetRule`'s UNTIL, SHIFTED by the drag, so a
        // series ending Aug 10 dragged two days later ended Aug 12.
        const endDate = mainRule.options.until;
        const sendsLeft = rebuildRule(mainRule).between(draggedDate, endDate, true).length;
        const endInTailBase = rebaseInstant(endDate, ruleAnchor, newRule.options.dtstart);
        newSet.rrule(boundTailToEndDate(newRule, endInTailBase, sendsLeft));
      } else {
        // If original was infinite, keep new series infinite
        newSet.rrule(newRule);
      }

      // Handle EXDATEs for new series
      const timeDiff = newDate.getTime() - draggedDate.getTime();
      const exdatesAfter = ruleSet.exdates()
        .filter(d => d >= draggedDate)
        .map(d => new Date(d.getTime() + timeDiff));
      exdatesAfter.forEach(d => newSet.exdate(d));

      const originalDueDate = this.formatDate(fromRuleTimeBase(mainRule.options.dtstart, ruleAnchor), true, true);
      const originalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(truncatedSet.rrules(), truncatedSet.exdates());
      const validatedOriginalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(originalNewRRule, mainRule.options.dtstart);

      const newDueDate = this.formatDate(newDate, true, true);
      const newRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(newSet.rrules(), newSet.exdates());
      const validatedNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(newRRule, new Date(newDueDate));

      return {
        originalAction: 'patch',
        originalDueDate: originalDueDate,
        originalNewRRule: validatedOriginalNewRRule,
        newAction: 'create',
        newDueDate: newDueDate,
        newRRule: validatedNewRRule
      };
    } 
    else {
      // For 'all' scope: every occurrence moves, so there is no HEAD to keep and no
      // TAIL to mint. Like `handleRecurringEdit`'s `all` and like the
      // `thisAndFollowing`-at-the-FIRST-occurrence branch above, this is a
      // RE-ANCHORING of a series that already has a document — so patch that document
      // in place rather than deleting it and building a replacement.
      //
      // Delete+create here was the last surviving instance of the shape those two were
      // moved off (FE#151 and its follow-up), and it fails the same way: the original
      // `_id` 404s so every reference to it dangles, the replacement inherits the
      // original's `createdAt`, the replacement is minted through the split-occurrence
      // path — which derives an OCCURRENCE-scoped `:occ:<from>:to:<to>` idempotency key,
      // the key the send path dedups on, and hangs it on a whole recurring series — and
      // a create that fails after the delete leaves the message gone.
      //
      // The drag dialog does not offer this scope today (`recurring-dialog-options.ts`
      // slices it off deliberately), so this is not a live customer path. It is one
      // config change away from being one, and the three siblings should not disagree.
      const newRule = (this.constructor as typeof RRuleRecurrenceHandler).offsetRule(mainRule, draggedDate, newDate, scope);
      const newSet = new RRuleSet();
      newSet.rrule(newRule);

      // Handle EXDATEs for the new series
      // Shift all EXDATEs by the time difference
      const timeDiff = newDate.getTime() - draggedDate.getTime();
      const exdates = ruleSet.exdates()
        .map(d => new Date(d.getTime() + timeDiff));
      exdates.forEach(d => newSet.exdate(d));

      const newDueDate = this.formatDate(newDate, true, true);
      const movedRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(newSet.rrules(), newSet.exdates());
      const validatedMovedRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(movedRRule, new Date(newDueDate));

      return {
        originalAction: 'patch',
        originalDueDate: newDueDate,
        originalNewRRule: validatedMovedRRule,
        newAction: 'none'
      };
    }
  }


  public handleDeletion(
    originalRuleStr: string,
    displayedDeletedDate: Date,
    scope: Scope,
    originalDateTime: Date
  ): RecurrenceHandlerResult {
    const tzid = tzidOfRuleString(originalRuleStr);
    if (!tzid) {
      return this.handleDeletionImpl(originalRuleStr, displayedDeletedDate, scope, originalDateTime);
    }
    FLOATING_TIME_BASE = true;
    try {
      const result = this.handleDeletionImpl(
        toFloatingRuleString(originalRuleStr),
        instantToFloating(displayedDeletedDate, tzid),
        scope,
        instantToFloating(originalDateTime, tzid),
      );
      return denormalizeTzResult(result, tzid);
    } finally {
      FLOATING_TIME_BASE = false;
    }
  }

  private handleDeletionImpl(
    originalRuleStr: string,
    displayedDeletedDate: Date,
    scope: Scope,
    originalDateTime: Date
  ): RecurrenceHandlerResult {
    if (isNaN(displayedDeletedDate.getTime())) {
      throw new Error('Invalid date format provided');
    }

    // Parse the original ruleset FIRST: the rule's own DTSTART is what defines the
    // rule's time base, and the conversions below need it.
    requireRuleAnchor(originalRuleStr);
    let ruleSet = rrulestr(originalRuleStr, { forceset: true }) as RRuleSet;
    const mainRule = ruleSet.rrules()[0];

    // As in handleDragAndDrop: exclude the instant the RULE emits, not the one the
    // calendar drew for it — and take the rule's offset from the RULE, not from the
    // stored dueDate. `originalDateTime` is the dueDate, which the backend keeps in
    // wall-clock terms (`calculateNextDuedate` -> `adjustForDSTChange`), so using it
    // as the reference made the correction cancel itself out on precisely the
    // series that had crossed a clock change. See handleDragAndDrop for the full note.
    const originalAnchor = new Date(mainRule.options.dtstart.getTime());

    // Re-seat the rule onto the document's current position so it speaks the right
    // time-of-day — spending the COUNT the series has already used, rather than
    // re-arming the whole budget from the later start. As in handleDragAndDrop, the
    // rebase may move the time base, so rule-time values come from the NEW anchor.
    rebaseDtstartConservingCount(mainRule, toRuleTimeBase(originalDateTime, originalAnchor));
    const ruleAnchor = new Date(mainRule.options.dtstart.getTime());
    const deletedDate = toRuleTimeBase(displayedDeletedDate, ruleAnchor);
    const originalRuleTime = toRuleTimeBase(originalDateTime, ruleAnchor);

    // Fix: Reconstruct the ruleset with the updated rule (EXDATEs re-expressed in
    // the new base -- see handleDragAndDrop).
    const fixedRuleSet = new RRuleSet();
    fixedRuleSet.rrule(rebuildRule(mainRule));
    ruleSet.exdates().forEach(exdate => fixedRuleSet.exdate(rebaseInstant(exdate, originalAnchor, ruleAnchor)));
    ruleSet = fixedRuleSet;

    // Use the explicitly provided originalDateTime
    const originalDueDate = this.formatDate(originalDateTime, true, true);

    switch (scope) {
      case 'this': {
        // Check if deleting the first occurrence
        const isFirstOccurrence = deletedDate.getTime() === mainRule.options.dtstart.getTime();
        
        if (isFirstOccurrence) {
          // Find the next valid occurrence after the first (accounting for existing EXDATEs)
          const secondOccurrence = ruleSet.after(deletedDate, false);
          
          if (!secondOccurrence) {
            // No second occurrence found - delete the entire series
            return {
              originalAction: 'delete',
              originalDueDate: originalDueDate,
              newAction: 'none'
            };
          }
          
          // Modify the existing series to start from the second occurrence
          const newOptions = { ...mainRule.options };
          newOptions.dtstart = secondOccurrence;
          
          // Update time-related options to match the new start time
          newOptions.byhour = [secondOccurrence.getUTCHours()];
          newOptions.byminute = [secondOccurrence.getUTCMinutes()];
          newOptions.bysecond = [secondOccurrence.getUTCSeconds()];
          
          // Adjust COUNT if present (reduce by 1 since we're removing the first occurrence)
          if (mainRule.options.count !== null && mainRule.options.count !== undefined) {
            newOptions.count = Math.max(1, mainRule.options.count - 1);
          }
          // For UNTIL and infinite series, no additional changes needed
          
          // Create the modified rule set
          const newRuleSet = new RRuleSet();
          newRuleSet.rrule(rebuildRule(mainRule, {
            dtstart: newOptions.dtstart,
            byhour: newOptions.byhour,
            byminute: newOptions.byminute,
            bysecond: newOptions.bysecond,
            count: newOptions.count,
          }));
          
          // Copy existing EXDATEs (excluding the one we're deleting)
          ruleSet.exdates()
            .filter(exdate => exdate.getTime() !== deletedDate.getTime())
            .forEach(exdate => newRuleSet.exdate(exdate));
          
          const modifiedRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(newRuleSet.rrules(), newRuleSet.exdates());
          const validatedModifiedRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(modifiedRRule, secondOccurrence);
          
          return {
            originalAction: 'patch',
            originalNewRRule: validatedModifiedRRule,
            // A rule instant on the way out is a wall-clock dueDate. (`validateRRule`
            // above deliberately keeps the RULE instant as its reference.)
            originalDueDate: this.formatDate(fromRuleTimeBase(secondOccurrence, ruleAnchor), true, true),
            newAction: 'none'
          };
        }
        
        // For non-first occurrences, use traditional EXDATE approach
        const exdate = new Date(deletedDate.getTime());
        ruleSet.exdate(exdate);
        const originalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(ruleSet.rrules(), ruleSet.exdates());
        const validatedOriginalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(originalNewRRule, originalRuleTime);

        return {
          originalAction: 'patch',
          originalNewRRule: validatedOriginalNewRRule,
          originalDueDate: originalDueDate,
          newAction: 'none'
        };
      }

      case 'thisAndFollowing': {
        // Check if this is the first occurrence using between()
        const firstOccurrence = mainRule.between(mainRule.options.dtstart, deletedDate, true)[0];
        const isFirstInstance = firstOccurrence &&
          firstOccurrence.getTime() === mainRule.options.dtstart.getTime() &&
          deletedDate.getTime() === mainRule.options.dtstart.getTime();

        // If first instance is deleted, delete entire series
        if (isFirstInstance) {
          return {
            originalAction: 'delete',
            originalDueDate: this.formatDate(originalDateTime, true, true),
            newAction: 'none'
          };
        }

        // Create truncated rule (omit count — we use until instead)
        const truncatedRuleSet = new RRuleSet();

        // Set UNTIL to 5 seconds before deleted instance for precise cutoff
        const untilDate = new Date(deletedDate.getTime() - 5000); // 5 seconds before

        truncatedRuleSet.rrule(rebuildRule(mainRule, { count: undefined, until: untilDate }));

        // Copy relevant exceptions
        const exdates = ruleSet.exdates();
        exdates.forEach(exdate => {
          if (exdate < deletedDate) {
            truncatedRuleSet.exdate(exdate);
          }
        });

        const originalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(truncatedRuleSet.rrules(), truncatedRuleSet.exdates());
        const validatedOriginalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(originalNewRRule, originalRuleTime);

        return {
          originalAction: 'patch',
          originalNewRRule: validatedOriginalNewRRule,
          originalDueDate: originalDueDate,
          newAction: 'none'
        };
      }

      case 'all':
        return {
          originalAction: 'delete',
          originalDueDate: originalDueDate,
          newAction: 'none'
        };

      default:
        throw new Error(`Invalid scope: ${scope}`);
    }
  }

  public handleRecurringEdit(
    originalRuleStr: string,
    clickedDate: Date,
    newDate: Date,
    newRuleStr: string,
    scope: Scope,
    originalDateTime: Date
  ): RecurrenceHandlerResult {
    const tzid = tzidOfRuleString(originalRuleStr);
    if (!tzid) {
      return this.handleRecurringEditImpl(originalRuleStr, clickedDate, newDate, newRuleStr, scope, originalDateTime);
    }
    FLOATING_TIME_BASE = true;
    try {
      const result = this.handleRecurringEditImpl(
        toFloatingRuleString(originalRuleStr),
        instantToFloating(clickedDate, tzid),
        instantToFloating(newDate, tzid),
        // The draft rule from the (born-correct) picker is itself a TZID rule; float it too.
        newRuleStr ? toFloatingRuleString(newRuleStr) : newRuleStr,
        scope,
        instantToFloating(originalDateTime, tzid),
      );
      return denormalizeTzResult(result, tzid);
    } finally {
      FLOATING_TIME_BASE = false;
    }
  }

  private handleRecurringEditImpl(
    originalRuleStr: string,
    clickedDate: Date,
    newDate: Date,
    newRuleStr: string,
    scope: Scope,
    originalDateTime: Date
  ): RecurrenceHandlerResult {

    requireRuleAnchor(originalRuleStr);
    const originalRuleSet = rrulestr(originalRuleStr, { forceset: true }) as RRuleSet;
    const originalMainRule = originalRuleSet.rrules()[0];

    // Same two bases as the drag and delete paths, and for the same reasons — see
    // `handleDragAndDrop` for the long note. This method used to be the odd one out
    // twice over:
    //
    //  - It never converted the clicked occurrence into the rule's time base at all,
    //    so on a series past a clock change the EXDATE it writes lands an hour off
    //    every instant the rule emits and excludes nothing.
    //  - It re-seated DTSTART with a RAW assignment where its two siblings call
    //    `rebaseDtstartConservingCount`, so a COUNT-bounded series that had already
    //    fired got its whole budget re-armed from the later start. Measured, on
    //    COUNT=5 weekly with two sends gone and the customer editing the third:
    //    delete and drag both leave 2 sends, this path left 4. Editing one
    //    occurrence silently bought the customer two sends they never asked for —
    //    the same defect the rebase helper was written for, in the one caller that
    //    did not use it.
    const originalAnchor = new Date(originalMainRule.options.dtstart.getTime());
    // The customer's end date as stored (an UNTIL-bounded series), in the stored
    // rule's own base -- read before the rebase shifts it.
    const storedUntil: Date | null = originalMainRule.options.until ? new Date(originalMainRule.options.until.getTime()) : null;
    // The stored rule's own reading of the due occurrence -- for anything that
    // keeps the ORIGINAL rule as it is (the whole-series content edit).
    const originalRuleTimeInStoredRule = toRuleTimeBase(originalDateTime, originalAnchor);

    // The rebase may move the rule's time base (see rebaseDtstartConservingCount),
    // so every rule-time value below comes from the anchor it leaves behind.
    rebaseDtstartConservingCount(originalMainRule, originalRuleTimeInStoredRule);
    const ruleAnchor = new Date(originalMainRule.options.dtstart.getTime());
    const clickedRuleTime = toRuleTimeBase(clickedDate, ruleAnchor);
    const originalRuleTime = toRuleTimeBase(originalDateTime, ruleAnchor);

    // Fix: Reconstruct the ruleset with the updated rule
    const fixedRuleSet = new RRuleSet();
    fixedRuleSet.rrule(rebuildRule(originalMainRule));
    originalRuleSet.exdates().forEach(exdate => fixedRuleSet.exdate(rebaseInstant(exdate, originalAnchor, ruleAnchor)));
    

    // Helper function to check if a date is the first occurrence
    const isFirstOccurrence = (date: Date): boolean => {
      const startDate = originalMainRule.options.dtstart;
      const endDate = new Date(startDate.getTime());
      endDate.setDate(endDate.getDate() + 1); // Look at just a 1-day window

      const firstOccurrence = originalMainRule.between(startDate, endDate, true)[0];
      const result = firstOccurrence &&
        firstOccurrence.getTime() === startDate.getTime() &&
        date.getTime() === startDate.getTime();
        
      
      return result;
    };


    switch (scope) {
      case 'all': {
        // For 'all' scope we PATCH the existing live pending doc in place — rewriting its
        // rruleset (which carries DTSTART) and dueDate to the new rule/time — instead of the
        // old delete-then-create. The backend walks each next occurrence from the doc's own
        // dueDate + rruleset (deleteUserMessageAndCreateLog -> calculateNextDuedate), so a
        // single patch redirects the whole series forward; no new root is minted.
        //
        // Why this changed (FE#151 follow-up): delete+create minted a SECOND chain-head on any
        // re-submit (no idempotency), and its create-then-delete ordering left a window where
        // original and replacement coexisted. Patch-in-place is idempotent under re-submit
        // (two patches converge to one doc) and removes that window entirely. Mirrors the
        // thisAndFollowing "first occurrence of weekday" branch, which already patches.
        //
        // A CONTENT edit of the whole series must not move the schedule. The draft's
        // dueDate is the occurrence the customer happened to CLICK, not the series'
        // position, so writing it back re-dated the document onto a middle
        // occurrence while DTSTART stayed at the start: the sender fires on dueDate,
        // so every earlier unsent occurrence silently never went out -- while the
        // calendar, which expands from DTSTART, kept drawing them. When the customer
        // changed neither the clicked occurrence's time nor the recurrence, the
        // stored rule and the stored dueDate ARE the schedule, and they stay.
        //
        // When they DID change the time or the recurrence, the pickers have already
        // rebuilt the draft's rule around the new time (DTSTART = that date), and
        // that rewrite is taken as before. That branch is NOT covered by this fix
        // and has known gaps: from a middle chip it restarts the series at the
        // clicked date, and the pickers copy the stored COUNT total onto it.
        const scheduleUnchanged =
          sameMinute(newDate, clickedDate) && ruleLineOf(newRuleStr) === ruleLineOf(originalRuleStr);

        if (scheduleUnchanged) {
          return {
            originalAction: 'patch' as const,
            originalDueDate: this.formatDate(originalDateTime, true, true),
            originalNewRRule: (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(originalRuleStr, originalRuleTimeInStoredRule),
            newAction: 'none' as const,
          };
        }

        const validatedNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(newRuleStr, newDate);
        const newDueDate = this.formatDate(newDate, true, true);

        const result = {
          originalAction: 'patch' as const,
          originalDueDate: newDueDate,
          originalNewRRule: validatedNewRRule,
          newAction: 'none' as const,
        };

        return result;
      }

      case 'this': {
        // For 'this' scope:
        // 1. Exclude the clicked date from original series
        // 2. Create ONE send at the new date.
        //
        // "Only this one" is a single occurrence, so the copy never carries a rule.
        // It used to take `newRuleStr` -- but that is the compose DRAFT's rruleset,
        // and for a recurring message the draft always holds the WHOLE series rule.
        // The copy was therefore created as a full series starting at the edited
        // occurrence: COUNT=5 edited at the third sent 7 messages, the last two
        // twice. `newRuleStr` is deliberately ignored here.
        const newRuleSet = new RRuleSet();
        newRuleSet.rrule(rebuildRule(originalMainRule));

        // Copy over any existing exdates from the original ruleSet
        fixedRuleSet.exdates().forEach(existingExdate => {
          newRuleSet.exdate(existingExdate);
        });

        // Add the new exdate for the clicked event — in the RULE's time base, or it
        // coincides with nothing and excludes nothing.
        newRuleSet.exdate(new Date(clickedRuleTime.getTime()));
        

        // Same re-seating as the drag path: editing the occurrence the document is
        // currently due at excludes that instant, so the dueDate must move on to one
        // the rule still emits or the backend sends the occurrence just edited away.
        const remainingDue = nextEmittedOnOrAfter(newRuleSet, originalRuleTime);
        if (!remainingDue) {
          // Nothing left in the series; the edited occurrence continues as the new
          // document created below.
          return {
            originalAction: 'delete' as const,
            originalDueDate: this.formatDate(originalDateTime, true, true),
            newAction: 'create' as const,
            newDueDate: this.formatDate(newDate, true, true),
            newRRule: undefined,
          };
        }

        // Format and validate the original series with the new exclusion
        const originalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(newRuleSet.rrules(), newRuleSet.exdates());
        // Rule instant out -> wall clock to be STORED; the rule instant itself stays
        // as `validateRRule`'s reference, which walks the rule.
        const originalDueDate = this.formatDate(fromRuleTimeBase(remainingDue, ruleAnchor), true, true);
        const validatedOriginalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(originalNewRRule, remainingDue);

          

        const result = {
          originalAction: 'patch' as const,
          originalDueDate: originalDueDate,
          originalNewRRule: validatedOriginalNewRRule,
          newAction: 'create' as const,
          newDueDate: this.formatDate(newDate, true, true),
          newRRule: undefined,  // one occurrence: a one-off, never the draft's series rule
        };
        
        return result;
      }

      case 'thisAndFollowing': {
        
        // Check if this is the first occurrence
        // The tail is the draft's rule RE-ANCHORED at the edited occurrence, carrying
        // exactly the sends the series has left from there, plus the exclusions that
        // fall inside it (moved with it). See `reanchorRule` for why it cannot keep
        // the draft's own DTSTART.
        const buildTail = (sendsLeft: number | undefined, isSplit: boolean): string | undefined => {
          if (!newRuleStr) return undefined;
          const draftRule = (rrulestr(newRuleStr, { forceset: true }) as RRuleSet).rrules()[0];
          const tailSet = new RRuleSet();
          let tailRule = reanchorRule(draftRule, newDate, clickedRuleTime, sendsLeft);
          // The same end-date ruling as the drag split: when the customer kept the
          // series' end date, the tail holds it and gains no send. (A draft with a
          // DIFFERENT end date is the customer choosing a new one -- taken as is.)
          const draftUntil = draftRule.options.until;
          const endUnchanged = !!storedUntil && !!draftUntil && draftUntil.getTime() === storedUntil.getTime();
          if (isSplit && endUnchanged && sendsLeft === undefined) {
            const endDate = originalMainRule.options.until!;
            const untilSends = rebuildRule(originalMainRule).between(clickedRuleTime, endDate, true).length;
            tailRule = boundTailToEndDate(tailRule, rebaseInstant(endDate, ruleAnchor, newDate), untilSends);
          }
          tailSet.rrule(tailRule);
          const shift = newDate.getTime() - clickedRuleTime.getTime();
          fixedRuleSet.exdates()
            .filter(d => d.getTime() >= clickedRuleTime.getTime())
            .forEach(d => tailSet.exdate(new Date(d.getTime() + shift)));
          const tail = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(tailSet.rrules(), tailSet.exdates());
          return (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(tail, newDate);
        };

        const isFirst = isFirstOccurrence(clickedRuleTime);
        if (isFirst) {
          // Nothing precedes the first occurrence, so this scope covers the whole
          // series: no head to keep, no tail to mint, and therefore no reason to
          // destroy a document and build another one. Re-anchor the one that exists —
          // same decision, and the same reasons, as the `all` scope below and as the
          // drag path's first-occurrence branch. See those for what delete+create
          // actually cost: a 404'd `_id`, an occurrence-scoped dedup key on a series,
          // and a failure mode in which the customer's message is simply gone.
          //
          // Re-anchored like any tail, with the COUNT the series has left (already
          // conserved by the rebase above), so a draft rule the pickers rebuilt with
          // the stored TOTAL cannot mint extra sends.
          const result = {
            originalAction: 'patch' as const,
            originalDueDate: this.formatDate(newDate, true, true),
            originalNewRRule: buildTail(originalMainRule.options.count ?? undefined, false),
            newAction: 'none' as const,
          };

          return result;
        }


        // Create truncated rule for the original series (omit count — we use until instead)
        // Set until to 1 minute before clicked date for precise cutoff
        const untilDate = new Date(clickedRuleTime.getTime());
        untilDate.setMinutes(untilDate.getMinutes() - 1);

        const truncatedSet = new RRuleSet();
        truncatedSet.rrule(rebuildRule(originalMainRule, { count: undefined, until: untilDate }));

        // Add any existing exceptions that occur before the clicked date
        const exdatesBefore = fixedRuleSet.exdates()
          .filter(d => d < clickedRuleTime)
          .map(d => new Date(d.getTime()));
        exdatesBefore.forEach(d => truncatedSet.exdate(d));
        

        // Remaining sends from the clicked occurrence on (COUNT-bounded series only).
        const remainingCount = (this.constructor as typeof RRuleRecurrenceHandler).calculateRemainingCount(originalMainRule, clickedRuleTime);

        // FIX: Use originalDateTime instead of dtstart
        const originalDueDate = this.formatDate(originalDateTime, true, true);
        const originalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).formatRuleSetToString(truncatedSet.rrules(), truncatedSet.exdates());
        const validatedOriginalNewRRule = (this.constructor as typeof RRuleRecurrenceHandler).validateRRule(originalNewRRule, originalRuleTime);

        const newDueDate = this.formatDate(newDate, true, true);
        const validatedNewRRule = buildTail(remainingCount, true);
        

        const result = {
          originalAction: 'patch' as const,
          originalDueDate: originalDueDate,
          originalNewRRule: validatedOriginalNewRRule,
          newAction: 'create' as const,
          newDueDate: newDueDate,
          newRRule: validatedNewRRule
        };
        
        return result;
      }

      default:
        throw new Error(`Invalid scope: ${scope}`);
    }
  }
} 